import { useState } from "react";
import {
  createManyOrganizations,
  createManyTickets,
  createManyUsers,
  getJobStatus
} from "../service/ticket";
import { useNotification } from "../components/notification";
import { useHistory } from "./useHistory";
import { getCurrentUserId } from "../service/zendesk";
import { useQueryClient } from "@tanstack/react-query";

const logger = {
  warning: (msg: string) => console.warn(`[BulkCreate] ${msg}`),
  error: (msg: string) => console.error(`[BulkCreate] ${msg}`),
  info: (msg: string) => console.info(`[BulkCreate] ${msg}`),
};

export interface BulkCreateProps {
  loading: boolean;
  setLoading: (isLoading: boolean) => void;
}

interface JobResult {
  job_id: string;
  status: "completed" | "failed" | "pending";
  success: boolean;
  results?: any[];
  error?: string;
  pending?: boolean;
  url?: string;
}

export function useBulkCreate({ loading, setLoading }: BulkCreateProps) {
  const [result, setResult] = useState<JobResult[] | null>(null);
  const { showNotification } = useNotification();
  const { createBulkHistory } = useHistory();
  const queryClient = useQueryClient();

  async function waitJobCompletion(job_id: string, auth: any): Promise<JobResult> {
    let retries = 0;
    const maxRetries = 400;

    while (true) {
      const res = await getJobStatus(job_id, auth);
      const job = res.data.job_status;
      const status = job.status;

      logger.info(`Job ${job_id} status: ${status}`);

      if (status === "completed") {
        return {
          job_id,
          status: "completed",
          success: true,
          results: job.results ?? [],
          url: job.url,
        };
      }

      if (status === "failed") {
        return {
          job_id,
          status: "failed",
          success: false,
          error: job.error || "Job failed",
        };
      }

      retries++;
      if (retries >= maxRetries) {
        return {
          job_id,
          status: "pending",
          success: true,
          pending: true,
        };
      }

      await new Promise(r => setTimeout(r, 2000));
    }
  }

  async function massiveCreate({
    payload,
    type
  }: {
    payload: any;
    type: "ticket" | "user" | "organization";
  }) {
    setLoading(true);
    setResult(null);

    try {
      showNotification({
        type: "info",
        title: "Aguarde um momento",
        message: "A criação massiva pode levar algum tempo, dependendo da quantidade de dados.",
      });

      const response =
        type === "ticket"
          ? await createManyTickets(payload)
          : type === "user"
            ? await createManyUsers(payload)
            : await createManyOrganizations(payload);

      const hasDuplicateErrors = response.data.duplicate_errors && response.data.duplicate_errors.length > 0;
      if (hasDuplicateErrors) {
        const duplicateMessages = response.data.duplicate_errors.map((err: any) =>
          `• ${err.name}: ${err.message} (CPF: ${err.cpf})`
        ).join("\n");
        const messageError = response.data.results?.map((r: any) => `• ${r.details}`).join("\n") || "Alguns itens falharam na criação. Verifique os detalhes.";

        showNotification({
          type: "warning",
          title: "CPF duplicado encontrado",
          message: `Encontrado(s) ${response.data.duplicate_errors.length} CPF(s) duplicado(s). Os usuários com CPF duplicado não serão criados:\n${duplicateMessages} ` + `\n\n${messageError}`,
        });
      }

      if (type === "user") {
        //obj de usersMap: trazer o name dos users quando der sucesso tb para tabela de resultados
        let usersMap: Record<string, any> = {};
        payload.users.forEach((user: any) => {
          const cpf = user.external_id;
          if (cpf) {
            usersMap[cpf] = user;
          }
        });
        const job_ids = response.data.job_ids;

        const finalResults = await Promise.all(
          job_ids.map((job_id: any) =>
            waitJobCompletion(job_id, {
              email: payload.email,
              token: payload.token,
              subdomain: payload.subdomain,
            })
          )
        );

        let mergedResults = finalResults.map(jobResult => ({
          ...jobResult,
          results: jobResult.results?.map((r: any) => {
            const originalUser = usersMap[r.external_id];

            return {
              ...r,
              name: originalUser?.name || r.name,
              email: originalUser?.email || r.email,
            };
          })
        }));

        const currentUserId = await getCurrentUserId();

        for (const jobResult of finalResults) {
          const total = jobResult.results?.length ?? 0;

          const successCount =
            jobResult.results?.filter((r: any) => r.status === "Created").length ?? 0;

          const failedCount = total - successCount;

          await createBulkHistory({
            action: "create",
            action_type: "user",
            requester: currentUserId,
            total_count: total,
            success_count: successCount,
            failed_count: failedCount,
            job_ids: job_ids.join(","),
            status:
              failedCount === 0
                ? "completed"
                : successCount === 0
                  ? "failed"
                  : "partial",
          });
        }
        if (hasDuplicateErrors) {
          const fakeErrorResults = response.data.duplicate_errors.map((err: any, index: number) => ({
            job_id: `duplicate-${index}`,
            status: "failed",
            success: false,
            results: [
              {
                id: null,
                name: err.name,
                email: "",
                status: "Failed",
                error: err.message,
                details: err.message
              }
            ]
          }));

          mergedResults = [...mergedResults, ...fakeErrorResults];
        }

        setResult(mergedResults);
        return mergedResults;
      }

      const job_ids: string[] = response.data.results.map(
        (r: any) => r.response.job_status.id
      );

      const finalResults: JobResult[] = [];
      const currentUserId = await getCurrentUserId();

      for (const job_id of job_ids) {
        const jobResult = await waitJobCompletion(job_id, {
          email: payload.email,
          token: payload.token,
          subdomain: payload.subdomain,
        });

        const total = jobResult.results?.length ?? 0;

        const successCount =
          jobResult.results?.filter(r => !!r.id).length ?? 0;

        const failedCount = total - successCount;

        await createBulkHistory({
          action: "create",
          action_type: type,
          requester: currentUserId,
          total_count: total,
          success_count: successCount,
          failed_count: failedCount,
          job_ids: job_ids.join(","),
          status:
            failedCount === 0
              ? "completed"
              : successCount === 0
                ? "failed"
                : "partial",
          error_summary:
            failedCount > 0 ? "Alguns itens falharam na criação" : undefined,
        });

        finalResults.push(jobResult);
      }

      queryClient.invalidateQueries({
        queryKey: ["bulk-history"]
      });

      setResult(finalResults);

      const hasFailures = finalResults.some(r => !r.success);
      const hasPendings = finalResults.some(r => r.pending);

      if (hasFailures) {
        showNotification({
          type: "error",
          title: "Falha na criação massiva",
          message: "Alguns itens não puderam ser processados. Verifique os detalhes.",
        });
      } else if (hasPendings) {
        showNotification({
          type: "warning",
          title: "Processamento em andamento",
          message: "Alguns jobs ainda estão sendo processados no Zendesk.",
        });
      } else {
        showNotification({
          type: "success",
          title: "Criação massiva concluída",
          message: "Todos os itens foram processados com sucesso.",
        });
      }

      return finalResults;

    } catch (err: any) {
      logger.error(err?.message || "Erro inesperado");
      const validationErrors = err.response?.data?.duplicate_errors;

      if (validationErrors && type === "user") {
        const fakeResults: JobResult[] = [
          {
            job_id: "validation-error",
            status: "failed",
            success: false,
            results: validationErrors.map((err: any) => ({
              id: null,
              name: err.name,
              email: "",
              status: "Failed",
              error: err.message,
              details: err.message,
            }))
          }
        ];

        setResult(fakeResults);
        return fakeResults;
      }

      throw err;
    } finally {
      setLoading(false);
    }
  }

  return { loading, massiveCreate, result };
}
